import React from 'react';
import { Navigate, createHashRouter } from 'react-router-dom';
import AccountCommunication from '../../../../../ui-kitchen/src/components/Account/AccountCommunication/AccountCommunication';
import AccountPaymentMethods from '../../../../../ui-kitchen/src/components/Account/Payment/AccountPaymentMethods/AccountPaymentMethods';
import AccountProfile from '../../../../../ui-kitchen/src/components/Account/AccountProfile/AccountProfile';
import AccountReservations from '../../../../../ui-kitchen/src/components/Account/AccountReservations/AccountReservations';
import AddPaymentController from '../AddPaymentPage/AddPaymentController';
import AuthController from '../../containers/AuthContainer/AuthController';
import AuthenticatedRoute from './AuthenticatedRoute';
import BookingController from '../BookingLoading/BookingController';
import Captcha from '../../../../../ui-kitchen/src/components/Captcha/Captcha';
import ChangeCancelControllerView from '../ConfirmationPage/ChangeCancelController';
import ConfirmationController from '../ConfirmationPage/ConfirmationController';
import ConfirmCancelControllerView from '../ConfirmCancelControllerView';
import ErrorPage from '../Error/ErrorPage';
import EventDetailsController from '../EventPage/EventDetailsController';
import HelpAndFeedback from '../../../../../ui-kitchen/src/components/Account/HelpAndFeedback/HelpAndFeedback';
import ReservationDeepLinkController from '../ReservationDeepLinkController';
import ReservationDeepLinkRedirect from '../ReservationDeepLinkRedirect';
import ReservationDetailsController from '../ReservationDetailsController/ReservationDetailsController';
import ReservationPathNaa from '../ReservationPathNaa/ReservationPathNaa';
import ResetPasswordController from '../ResetPasswordController/ResetPasswordController';
import UpdatePhoneContainer from '../../../../../ui-kitchen/src/components/Account/UpdatePhone/UpdatePhoneContainer';
import VenueDetailsController from '../VenueDetailsControllerView/VenueDetailsController';
import VenueListing from '../VenueListing/VenueListing';
import WidgetRouteOutlet from './WidgetRouteOutlet';

import { getLocationPath } from '../../../../../ui-kitchen/src/utils/locationUtils';
import APP_CONTEXT from '../../../../../ui-kitchen/src/constants/appContext';

// ** Routing Setup After Widget Migration from Angular:
// Using the newer ReactRouter v6 browser router setup that uses plain objects with RouterProvider instead of
// specific Router/Route components in order to take advantage of all the features new API, in particular, useMatches.

// The `handle` value is purposed here to carry over Angular routes' `resolve` prop for each route.
// For now, these values are dispatched, but not yet wired up to new behavior.
const createWidgetHashRouter = ({
  Analytics, AutomationMode, ResyAmexConfig, preferredLanguage,
}) => {
  const triggerCycle = () => { };
  const $routeReloadShim = { reload: window.location.reload };
  const $rootScopePubSubShim = { $broadcast: () => { }, $on: () => { } };
  const routes = [
    {
      element: <WidgetRouteOutlet Analytics={Analytics} />,
      children: [
        {
          path: '/list-venues',
          element: <VenueListing />,
          handle: {
            showNav: false,
            titleKey: 'all_restaurants',
            pageName: 'List Venues',
            wrapperClass: 'page-venue-listing',
          },
        },
        {
          path: '/venues/:venueId',
          element: <VenueDetailsController
            Analytics={Analytics}
            ResyAmexConfig={ResyAmexConfig}
          />,
          handle: {
            hasMetadataPageTitle: true,
            pageContext: 'browse tab',
            pageName: 'Venue Details',
            showNav: true,
            showBack: false,
            showLogo: false,
            showProfileMenu: true,
            titleKey: 'restaurant_reservations',
            wrapperClass: 'page-venue-details',
          },
        },
        {
          path: '/cities/:locationId/venues/:venueSlug',
          element: <VenueDetailsController
            Analytics={Analytics}
            ResyAmexConfig={ResyAmexConfig}
          />,
          handle: {
            hasMetadataPageTitle: true,
            pageName: 'Venue Details',
            showNav: true,
            showBack: false,
            showLogo: false,
            showProfileMenu: true,
            titleKey: 'restaurant_reservations',
            wrapperClass: 'page-venue-details',
          },
        },
        {
          path: '/venues/:venueId/:shopsmall',
          element: <VenueDetailsController
            Analytics={Analytics}
            ResyAmexConfig={ResyAmexConfig}
          />,
          handle: {
            hasMetadataPageTitle: true,
            pageContext: 'browse tab',
            pageName: 'Venue Details',
            showNav: true,
            showBack: false,
            showLogo: false,
            showProfileMenu: true,
            titleKey: 'restaurant_reservations',
            wrapperClass: 'page-venue-details',
          },
        },
        {
          path: '/venues/:venueId/add-ons/:templateId?',
          element: <VenueDetailsController
            Analytics={Analytics}
            ResyAmexConfig={ResyAmexConfig}
            isAddOns
          />,
          handle: {
            backPath: 'AddOns',
            hasMetadataPageTitle: true,
            pageName: 'Add Ons',
            search: true,
            showNav: false,
            showBack: true,
            showLogo: false,
            showProfileMenu: true,
            titleKey: 'restaurant_reservations',
          },
        },
        {
          path: '/captcha',
          element: <Captcha
            AutomationMode={AutomationMode}
          />,
          handle: {
            pageName: 'Security Verification',
            showNav: true,
            showBack: false,
            showLogo: false,
            showProfileMenu: true,
          },
        },
        {
          path: '/reservation-details',
          element: <ReservationDetailsController
            Analytics={Analytics}
            preferredLanguage={preferredLanguage}
            triggerCycle={triggerCycle}
          />,
          handle: {
            backPath: 'Summary',
            hasMetadataPageTitle: true,
            pageName: 'Reservation Details',
            search: true,
            showNav: false,
            showBack: true,
            showLogo: false,
            showProfileMenu: true,
            titleKey: 'reservation_details',
            wrapperClass: 'page-reservation-details',
          },
        },
        {
          path: '/book',
          element: <BookingController />,
          handle: {
            pageName: 'Book',
            search: true,
            showNav: true,
            showBack: false,
            showLogo: false,
            showProfileMenu: true,
          },
        },
        {
          path: '/credit-card-confirmed',
          element: <ReservationDetailsController
            Analytics={Analytics}
            preferredLanguage={preferredLanguage}
            triggerCycle={triggerCycle}
            isCreditCardConfirmed
          />,
          handle: {
            backPath: 'Payment',
            pageName: 'Select Credit Card Page',
            search: true,
            showNav: false,
            showBack: true,
            showLogo: false,
            showProfileMenu: true,
            titleKey: 'select_credit_card_page',
            wrapperClass: 'page-reservation-details',
          },
        },
        {
          path: '/add-payment',
          element: <AddPaymentController
            Analytics={Analytics}
            AutomationMode={AutomationMode}
          />,
          handle: {
            backPath: 'Payment',
            pageName: 'Add Payment',
            showNav: false,
            titleKey: 'add_payment',
          },
        },
        {
          path: '/confirmation',
          element: <ConfirmationController
            Analytics={Analytics}
            preferredLanguage={preferredLanguage}
          />,
          handle: {
            pageContext: 'confirmation screen',
            search: true,
            showNav: false,
            showBack: true,
            showLogo: false,
            showProfileMenu: true,
            pageName: 'Confirmation',
            titleKey: 'reservation_confirmed',
            wrapperClass: 'page-confirmation',
          },
        },
        {
          path: '/cities/:location/venues/:venueSlug/events/:eventSlug',
          element: <EventDetailsController
            Analytics={Analytics}
            ResyAmexConfig={ResyAmexConfig}
          />,
          handle: {
            eventPath: getLocationPath(),
            hasMetadataPageTitle: true,
            pageName: 'Event Details',
            showNav: false,
            showProfileMenu: true,
          },
        },
        {
          element: <AuthenticatedRoute />,
          children: [
            {
              path: '/account/profile',
              element: <AccountProfile context={APP_CONTEXT.WIDGET} Analytics={Analytics} $rootScope={$rootScopePubSubShim} />,
              handle: {
                backPath: 'Profile',
                $rootScope: { $rootScopePubSubShim },
                pageName: 'Account Profile',
                showNav: true,
                showBack: true,
                showLogo: true,
                showProfileMenu: true,
                titleKey: 'profile',
                wrapperClass: 'resy-account-profile',
              },
            },
            {
              path: '/account/communication',
              element: <AccountCommunication context={APP_CONTEXT.WIDGET} Analytics={Analytics} />,
              handle: {
                backPath: 'Profile',
                pageName: 'Account Communications',
                showNav: true,
                showBack: true,
                showLogo: true,
                showProfileMenu: true,
                titleKey: 'communication',
                wrapperClass: 'resy-account-communication',
              },
            },
            {
              path: '/account/reservations-and-notify',
              element: <AccountReservations
                context={APP_CONTEXT.WIDGET}
                $rootScope={$rootScopePubSubShim}
                $route={$routeReloadShim}
                Analytics={Analytics}
              />,
              handle: {
                backPath: 'Profile',
                pageName: 'Account Reservations &amp; Notify',
                showNav: true,
                showBack: true,
                showLogo: true,
                showProfileMenu: true,
                titleKey: 'reservations_and_notify',
                wrapperClass: 'resy-account-reservations',
              },
            },
            {
              path: '/account/concierge-reservations',
              element: <AccountReservations
                context={APP_CONTEXT.WIDGET}
                bookOnBehalfOf
                $rootScope={$rootScopePubSubShim}
                $route={$routeReloadShim}
                Analytics={Analytics}
              />,
              handle: {
                backPath: 'Profile',
                pageName: 'Account Concierge Reservations',
                showNav: true,
                showBack: true,
                showLogo: true,
                showProfileMenu: true,
                titleKey: 'concierge_reservations',
                wrapperClass: 'resy-concierge-reservations',
              },
            },
            {
              path: '/account/reservations',
              element: <Navigate to="/account/reservations-and-notify" replace />,
            },
            {
              path: '/account/help-and-feedback',
              element: <HelpAndFeedback context={APP_CONTEXT.WIDGET} />,
              handle: {
                backPath: 'Profile',
                pageName: 'Help and Feedback',
                showNav: true,
                showBack: true,
                showLogo: true,
                showProfileMenu: true,
                titleKey: 'help_and_feedback',
                wrapperClass: 'resy-help-and-feedback',
              },
            },
            {
              path: '/account/payment-methods',
              element: <AccountPaymentMethods
                Analytics={Analytics}
                AutomationMode={AutomationMode}
                context={APP_CONTEXT.WIDGET}
              />,
              handle: {
                backPath: 'Profile',
                pageName: 'Account Payment Methods',
                showNav: true,
                showBack: true,
                showLogo: true,
                showProfileMenu: true,
                titleKey: 'payment_methods',
                wrapperClass: 'resy-account-payment-methods',
              },
            },
            {
              path: '/account/reservation/cancel',
              element: <ConfirmCancelControllerView />,
              handle: {
                pageName: 'Account Reservation Cancel',
                showNav: true,
                showBack: false,
                showLogo: false,
                showProfileMenu: true,
                titleKey: 'reservation_cancelled',
                wrapperClass: 'page-confirm-cancel',
              },
            },
            {
              path: '/account/reservations-and-notify/:resyToken/:bookOnBehalfOf?',
              element: <ReservationDeepLinkController />,
              handle: {},
            },
            {
              path: '/account/reservations/:resyToken/:bookOnBehalfOf?',
              element: <ReservationDeepLinkRedirect />,
            },
            {
              path: '/account/change-cancel',
              element: <ChangeCancelControllerView
                Analytics={Analytics}
                preferredLanguage={preferredLanguage}
                triggerCycle={triggerCycle}
              />,
              handle: {
                backPath: 'ChangeCancel',
                pageName: 'Account Change/Cancel',
                showNav: false,
                showBack: true,
                showLogo: false,
                showProfileMenu: true,
                titleKey: 'manage_reservation',
                wrapperClass: 'page-user-reservation page-confirmation',
              },
            },
          ],
        },
        {
          path: '/login',
          element: <AuthController
            Analytics={Analytics}
            AutomationMode={AutomationMode}
            ResyAmexConfig={ResyAmexConfig}
          />,
          handle: {
            backPath: 'Profile',
            pageName: 'Login',
            showNav: false,
            showBack: true,
            showLogo: true,
            showProfileMenu: false,
            titleKey: 'login',
          },
        },
        {
          path: '/signup',
          element: <AuthController
            Analytics={Analytics}
            AutomationMode={AutomationMode}
            ResyAmexConfig={ResyAmexConfig}
          />,
          handle: {
            backPath: 'Profile',
            pageName: 'Signup',
            showNav: false,
            showBack: true,
            showLogo: true,
            showProfileMenu: false,
            titleKey: 'signup',
          },
        },
        {
          path: '/reservation-path-naa',
          element: <ReservationPathNaa
            Analytics={Analytics}
            preferredLanguage={preferredLanguage}
          />,
          handle: {
            pageContext: 'reservation path',
            showNav: false,
            showBack: true,
            showLogo: false,
            showProfileMenu: true,
            pageName: 'Reservation Path',
            titleKey: 'reservation_confirmed',
            wrapperClass: 'page-confirmation',
          },
        },
        {
          path: '/update-phone',
          element: <UpdatePhoneContainer
            Analytics={Analytics}
            preferredLanguage={preferredLanguage}
            context={APP_CONTEXT.WIDGET}
          />,
          handle: {
            pageContext: 'Update Phone',
            showNav: false,
            showBack: true,
            showLogo: false,
            showProfileMenu: true,
            pageName: 'Update Phone',
            titleKey: 'update_phone',
            wrapperClass: 'page-confirmation',
          },
        },
        {
          path: '/reset-password/:key?',
          element: <ResetPasswordController
            Analytics={Analytics}
            context={APP_CONTEXT.WIDGET}
          />,
          handle: {
            pageName: 'Reset Password',
            showNav: true,
            showBack: true,
            showLogo: true,
            showProfileMenu: false,
          },
        },
        { path: '/password', element: <Navigate to="/reset-password" /> },
        {
          path: '/error',
          element: <ErrorPage />,
          handle: {
            pageName: 'Error',
            showNav: false,
            titleKey: 'error',
            wrapperClass: 'page-error',
          },
        },
        { path: '*', element: <Navigate to="/error" replace /> },
      ],
    },
  ];
  return createHashRouter(routes);
};

export default createWidgetHashRouter;
